import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import Icon from "core/icon/Icon";
import InputText from "core/inputs/input-text/InputText";
import LoaderV2 from "core/loader/LoaderV2";
import { Typography } from "core/typography/Typography";
import { ORDER_DIR, POPUP_STATUS, QUERY_STATUS } from "constants/constants";
import {
  DELIVERY_NOTE_CONFIRM_MODAL_MESSAGES,
  DELIVERY_NOTE_PARTIES_DETAIL,
  DELIVERY_NOTE_SAVED_DELETE,
  DELIVERY_NOTE_SAVED_LIST_ACTIONS,
  DELIVERY_NOTE_SAVED_LIST_MESSAGES,
} from "constants/forms";
import { DELIVERY_NOTE_CURRENCY_LOAD_ERROR } from "constants/popup-codes";
import InfoContext from "pages/InfoContext";
import DeleteModal from "components/modal/DeleteModal";
import { useUserContext } from "api/user/UserProvider";
import { useCurrencyContext } from "api/settings/currency/CurrencyProvider";
import { formatDate } from "utils/helper";
import { sortHandler, sortTriggerHelper } from "utils/toolsManagerHelper";
import {
  filterDeliveryNotes,
  formatDeliveryNoteAmount,
  getCurrencySymbol,
  hasLinkedColumn,
} from "utils/deliveryNoteHelper";
import DeliveryNoteSavedNoteExport from "./DeliveryNoteSavedNoteExport";
import DeliveryNoteSavedNoteModal from "./DeliveryNoteSavedNoteModal";

const EMPTY_VALUE = "—";

const COLUMNS = [
  ["number", DELIVERY_NOTE_SAVED_LIST_MESSAGES.columnNumber, "number"],
  [
    "orderNumber",
    DELIVERY_NOTE_SAVED_LIST_MESSAGES.columnOrderNumber,
    "order-number",
  ],
  ["template", DELIVERY_NOTE_SAVED_LIST_MESSAGES.columnTemplate, "template-name"],
  ["parties", DELIVERY_NOTE_SAVED_LIST_MESSAGES.columnParties, "recipient"],
  [
    "deliveryDate",
    DELIVERY_NOTE_SAVED_LIST_MESSAGES.columnDeliveryDate,
    "delivery-date",
  ],
  ["total", DELIVERY_NOTE_SAVED_LIST_MESSAGES.columnTotal, "total-ht"],
  ["actions", DELIVERY_NOTE_SAVED_LIST_MESSAGES.columnActions],
];

const ARIA_SORT = {
  [ORDER_DIR.ASC]: "ascending",
  [ORDER_DIR.DESC]: "descending",
};

const DeliveryNoteSavedList = ({
  deliveryNotes,
  loadStatus,
  onSort,
  onEdit,
  onDelete,
  isDeleting,
}) => {
  const intl = useIntl();
  const { setCode, setStatus, confirmAction } = useContext(InfoContext);
  const previewLabel = intl.formatMessage(
    DELIVERY_NOTE_SAVED_LIST_ACTIONS.preview
  );
  const editLabel = intl.formatMessage(DELIVERY_NOTE_SAVED_LIST_ACTIONS.edit);
  const pdfLabel = intl.formatMessage(DELIVERY_NOTE_SAVED_LIST_ACTIONS.pdf);
  const deleteLabel = intl.formatMessage(
    DELIVERY_NOTE_SAVED_LIST_ACTIONS.delete
  );
  const { lang } = useUserContext();
  const { currencyListGetter, currencyList } = useCurrencyContext();

  const columnLabel = useMemo(
    () =>
      Object.fromEntries(
        COLUMNS.map(([key, message]) => [key, intl.formatMessage(message)])
      ),
    [intl]
  );

  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({});
  const [activeSortKey, setActiveSortKey] = useState(null);
  const isSearching = !!search.trim();
  const visibleNotes = useMemo(
    () => filterDeliveryNotes(deliveryNotes, { name: search }),
    [deliveryNotes, search]
  );

  const requestSort = (sortKey) => {
    setActiveSortKey(sortKey);
    sortHandler({
      sortKey,
      currentSort: sort[sortKey] ?? ORDER_DIR.DESC,
      setSort,
      getter: onSort,
    });
  };

  const [deletedNote, setDeletedNote] = useState(null);
  const [previewedNote, setPreviewedNote] = useState(null);
  const [exportedNote, setExportedNote] = useState(null);

  const endExport = useCallback(() => setExportedNote(null), []);

  const confirmDelete = async () => {
    const note = deletedNote;
    setDeletedNote(null);
    const deleted = await onDelete({ id: note.id });
    confirmAction({
      status: deleted ? QUERY_STATUS.SUCCESS : QUERY_STATUS.ERROR,
      messages: DELIVERY_NOTE_CONFIRM_MODAL_MESSAGES.noteDelete,
    });
  };

  useEffect(() => {
    currencyListGetter().catch(() => {
      setStatus(POPUP_STATUS.ERROR);
      setCode(DELIVERY_NOTE_CURRENCY_LOAD_ERROR);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderTotal = (note) => {
    if (!hasLinkedColumn(note.columns)) return null;
    const symbol = getCurrencySymbol({
      currencyList,
      currencyId: note.currency_id,
    });
    return (
      <Typography
        className="text-3 delivery-note-saved-notes__total"
        type="span"
        label={formatDeliveryNoteAmount({
          intl,
          amount: note.total_ht,
          currencySymbol: symbol,
        })}
      />
    );
  };

  return (
    <section
      className="delivery-note-saved-notes"
      id="delivery-note-saved-notes"
    >
      <Typography
        className="title-3 violet text-uppercase"
        type="h1"
        label={
          <FormattedMessage {...DELIVERY_NOTE_SAVED_LIST_MESSAGES.title} />
        }
      />
      <Typography
        className="title-5 light-slate-gray"
        type="p"
        label={
          <FormattedMessage {...DELIVERY_NOTE_SAVED_LIST_MESSAGES.subtitle} />
        }
      />

      <div className="delivery-note-saved-notes__search">
        <InputText
          errors={{}}
          id="delivery-note-saved-notes-search"
          dataTestId="delivery-note-saved-notes-search"
          placeholder={DELIVERY_NOTE_SAVED_LIST_MESSAGES.search}
          onChange={(e) => setSearch(e.target.value)}
          value={search}
        />
      </div>

      {loadStatus === QUERY_STATUS.LOADING && !deliveryNotes.length && (
        <LoaderV2 id="delivery-note-saved-notes-loader" />
      )}

      {loadStatus === QUERY_STATUS.ERROR && (
        <Typography
          className="text-3 alizarin-crimson"
          type="p"
          label={
            <FormattedMessage
              {...DELIVERY_NOTE_SAVED_LIST_MESSAGES.loadError}
            />
          }
        />
      )}

      {loadStatus === QUERY_STATUS.SUCCESS && !visibleNotes.length && (
        <Typography
          className="text-3 violet"
          type="p"
          label={
            <FormattedMessage
              {...(isSearching
                ? DELIVERY_NOTE_SAVED_LIST_MESSAGES.noMatch
                : DELIVERY_NOTE_SAVED_LIST_MESSAGES.empty)}
            />
          }
        />
      )}

      {!!visibleNotes.length && (
        <div className="delivery-note-saved-notes__table-scroll">
          <table className="delivery-note-saved-notes__table">
            <thead>
              <tr>
                {COLUMNS.map(([key, message, sortKey]) => (
                  <th
                    key={key}
                    scope="col"
                    aria-sort={
                      sortKey
                        ? (activeSortKey === sortKey &&
                            ARIA_SORT[sort[sortKey]]) ||
                          "none"
                        : undefined
                    }
                  >
                    {sortKey ? (
                      <button
                        type="button"
                        className="delivery-note-saved-notes__sort"
                        onClick={() => requestSort(sortKey)}
                        data-testid={`delivery-note-saved-notes-sort-${key}`}
                      >
                        <FormattedMessage {...message} />
                        <Icon
                          icon={sortTriggerHelper({
                            header: sort[sortKey],
                            sortCondition: ORDER_DIR.DESC,
                          })}
                          color={
                            activeSortKey === sortKey ? "violet" : "gray-200"
                          }
                          width={12}
                          height={12}
                        />
                      </button>
                    ) : (
                      <FormattedMessage {...message} />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleNotes.map((note) => (
                <tr
                  key={note.id}
                  data-testid={`delivery-note-saved-note-${note.id}`}
                >
                  <td data-label={columnLabel.number}>
                    <Typography
                      className="text-3 violet"
                      type="span"
                      label={note.number}
                    />
                  </td>
                  <td data-label={columnLabel.orderNumber}>
                    <Typography
                      className="text-3 violet"
                      type="span"
                      label={note.order_number || EMPTY_VALUE}
                    />
                  </td>
                  <td data-label={columnLabel.template}>
                    <Typography
                      className="text-3 violet"
                      type="span"
                      label={note.template_name}
                    />
                  </td>
                  <td data-label={columnLabel.parties}>
                    <Typography
                      className="text-3 violet"
                      type="span"
                      label={
                        <FormattedMessage
                          {...DELIVERY_NOTE_PARTIES_DETAIL}
                          values={{
                            issuer: note.issuer?.company_name || EMPTY_VALUE,
                            recipient:
                              note.recipient?.company_name || EMPTY_VALUE,
                          }}
                        />
                      }
                    />
                  </td>
                  <td
                    className="delivery-note-saved-notes__date"
                    data-label={columnLabel.deliveryDate}
                  >
                    <Typography
                      className="text-3 violet"
                      type="span"
                      label={
                        note.delivery_date
                          ? formatDate(note.delivery_date, lang)
                          : EMPTY_VALUE
                      }
                    />
                  </td>
                  <td data-label={columnLabel.total}>
                    {renderTotal(note) || (
                      <Typography
                        className="text-3 violet"
                        type="span"
                        label={EMPTY_VALUE}
                      />
                    )}
                  </td>
                  <td data-label={columnLabel.actions}>
                    <div className="delivery-note-saved-notes__buttons">
                      <button
                        type="button"
                        className="delivery-note-saved-notes__action"
                        onClick={() => setPreviewedNote(note)}
                        aria-label={previewLabel}
                        title={previewLabel}
                        data-testid={`delivery-note-saved-note-preview-${note.id}`}
                      >
                        <Icon icon="eye" color="violet" />
                      </button>
                      <button
                        type="button"
                        className="delivery-note-saved-notes__action"
                        onClick={() => onEdit(note)}
                        aria-label={editLabel}
                        title={editLabel}
                        data-testid={`delivery-note-saved-note-edit-${note.id}`}
                      >
                        <Icon icon="pencil" color="violet" />
                      </button>
                      <button
                        type="button"
                        className="delivery-note-saved-notes__action"
                        onClick={() => setExportedNote(note)}
                        disabled={!!exportedNote || !note.number}
                        aria-label={pdfLabel}
                        title={pdfLabel}
                        data-testid={`delivery-note-saved-note-pdf-${note.id}`}
                      >
                        <Icon icon="pdf-icon" color="violet" />
                      </button>
                      <button
                        type="button"
                        className="delivery-note-saved-notes__action"
                        onClick={() => setDeletedNote(note)}
                        disabled={isDeleting}
                        aria-label={deleteLabel}
                        title={deleteLabel}
                        data-testid={`delivery-note-saved-note-delete-${note.id}`}
                      >
                        <Icon icon="trash-2" color="red" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {previewedNote && (
        <DeliveryNoteSavedNoteModal
          note={previewedNote}
          onClose={() => setPreviewedNote(null)}
        />
      )}

      {exportedNote && (
        <DeliveryNoteSavedNoteExport note={exportedNote} onDone={endExport} />
      )}

      {deletedNote && (
        <DeleteModal
          open
          onClose={() => setDeletedNote(null)}
          onValidate={confirmDelete}
          validationMessages={DELIVERY_NOTE_SAVED_DELETE.validationMessages}
          highlight={deletedNote.number}
        />
      )}
    </section>
  );
};

DeliveryNoteSavedList.propTypes = {
  deliveryNotes: PropTypes.array.isRequired,
  loadStatus: PropTypes.string,
  onSort: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isDeleting: PropTypes.bool,
};

export default DeliveryNoteSavedList;
